<template>
  <div class="ad-page">
    <!-- Salud de los servicios -->
    <section>
      <div class="ad-toolbar sec-head">
        <h2 class="sec-title">Estado de los servicios</h2>
        <span v-if="health" class="overall" :class="`overall--${health.overall}`">
          <i class="dot" :class="`dot--${health.overall}`"></i>{{ OVERALL_TEXT[health.overall] }}
        </span>
        <span class="ad-toolbar__spacer"></span>
        <span v-if="health" class="ad-hint">Revisado {{ relativeTime(health.checkedAt) }}</span>
        <button type="button" class="ad-btn ad-btn--ghost" :disabled="healthLoading" @click="loadHealth">
          {{ healthLoading ? 'Revisando…' : 'Revisar de nuevo' }}
        </button>
      </div>
      <p v-if="healthError" class="ad-error">{{ healthError }}</p>
      <div class="checks">
        <article v-for="c in health ? health.checks : []" :key="c.key" class="ad-card check">
          <i class="dot" :class="`dot--${c.level}`" :title="LEVEL_TEXT[c.level]"></i>
          <div class="check__body">
            <span class="check__label">{{ c.label }}</span>
            <span class="check__detail">{{ c.detail }}</span>
          </div>
          <span class="check__level" :class="`check__level--${c.level}`">{{ LEVEL_TEXT[c.level] }}</span>
        </article>
      </div>
    </section>

    <!-- Errores del servidor -->
    <section>
      <div class="ad-toolbar sec-head">
        <h2 class="sec-title">Errores del servidor</h2>
        <span v-if="pending" class="ad-pill ad-pill--red">{{ pending }} sin revisar</span>
        <span class="ad-toolbar__spacer"></span>
        <label class="toggle">
          <input v-model="includeReviewed" type="checkbox" @change="loadErrors" />
          Mostrar revisados
        </label>
      </div>
      <div class="ad-card">
        <p v-if="errorsLoading && !errors.length" class="ad-empty">Cargando…</p>
        <p v-else-if="errorsError" class="ad-empty">{{ errorsError }}</p>
        <p v-else-if="!errors.length" class="ad-empty">{{ includeReviewed ? 'No hay errores registrados.' : 'No hay errores pendientes de revisar.' }}</p>
        <ul v-else class="errors">
          <li v-for="e in errors" :key="e.id" class="err" :class="{ 'err--reviewed': e.reviewed }">
            <div class="err__row">
              <span class="ad-mono err__ref">{{ e.ref }}</span>
              <div class="err__main">
                <span class="err__title"><b>{{ e.method }}</b> <span class="ad-mono">{{ e.path }}</span></span>
                <span class="err__meta">
                  {{ e.message }} · {{ formatDateTime(e.createdAt) }}<template v-if="e.parkName"> · {{ e.parkName }}</template>
                </span>
              </div>
              <button v-if="e.details" type="button" class="ad-btn ad-btn--ghost small" @click="toggle(e.id)">
                {{ open[e.id] ? 'Ocultar detalle' : 'Ver detalle' }}
              </button>
              <button v-if="!e.reviewed" type="button" class="ad-btn ad-btn--primary small" :disabled="busy[e.id]" @click="markReviewed(e)">
                Marcar como revisado
              </button>
              <span v-else class="ad-hint">Revisado por {{ e.reviewedBy }}</span>
            </div>
            <pre v-if="open[e.id]" class="err__details">{{ e.details }}</pre>
          </li>
        </ul>
        <p v-if="errors.length" class="ad-hint footnote">Se muestran los 20 más recientes. Los registros se conservan 90 días.</p>
      </div>
    </section>

    <!-- Comunicaciones -->
    <section>
      <div class="ad-toolbar sec-head">
        <h2 class="sec-title">Comunicaciones</h2>
        <span class="ad-toolbar__spacer"></span>
        <button v-if="comms && (comms.counts.failed || comms.counts.skipped)" type="button" class="ad-btn ad-btn--ghost" :disabled="retrying" @click="retry()">
          {{ retrying ? 'Reenviando…' : 'Reenviar todos los pendientes de envío' }}
        </button>
      </div>
      <div class="ad-card">
        <p v-if="commsLoading && !comms" class="ad-empty">Cargando…</p>
        <p v-else-if="commsError" class="ad-empty">{{ commsError }}</p>
        <template v-else-if="comms">
          <div class="comms">
            <div class="comms__prov">
              <span class="comms__label">Correo</span>
              <span class="ad-pill" :class="comms.providers.email.live ? 'ad-pill--green' : 'ad-pill--gray'">
                {{ comms.providers.email.error ? 'Con error' : comms.providers.email.live ? comms.providers.email.name : 'Modo consola' }}
              </span>
              <span class="comms__label">WhatsApp</span>
              <span class="ad-pill" :class="comms.providers.whatsapp.live ? 'ad-pill--green' : 'ad-pill--gray'">
                {{ comms.providers.whatsapp.live ? comms.providers.whatsapp.name : 'Modo consola' }}
              </span>
            </div>
            <div class="comms__counts">
              <div class="count"><b class="ad-mono">{{ comms.counts.sent24h }}</b><span>Enviados (24 h)</span></div>
              <div class="count"><b class="ad-mono">{{ comms.counts.pending }}</b><span>En cola</span></div>
              <div class="count" :class="{ 'count--warn': comms.counts.skipped }"><b class="ad-mono">{{ comms.counts.skipped }}</b><span>Sin enviar</span></div>
              <div class="count" :class="{ 'count--bad': comms.counts.failed }"><b class="ad-mono">{{ comms.counts.failed }}</b><span>Fallidos</span></div>
            </div>
          </div>
          <p v-if="!comms.providers.email.live && !comms.providers.email.error" class="ad-hint comms__note">
            Sin proveedor de correo configurado, los mensajes no salen: quedan como "Sin enviar" y se pueden reenviar cuando lo configures (EMAIL_PROVIDER y RESEND_API_KEY en el .env del Backend).
          </p>
          <p v-if="comms.providers.email.error" class="ad-error comms__note">{{ comms.providers.email.error }}</p>

          <ul v-if="comms.recent.length" class="errors">
            <li v-for="m in comms.recent" :key="m.id" class="err">
              <div class="err__row">
                <span class="ad-pill" :class="m.status === 'FAILED' ? 'ad-pill--red' : 'ad-pill--amber'">{{ m.status === 'FAILED' ? 'Fallido' : 'Sin enviar' }}</span>
                <div class="err__main">
                  <span class="err__title">
                    <b>{{ m.channel === 'EMAIL' ? 'Correo' : 'WhatsApp' }}</b> · {{ m.subject || TEMPLATE_LABEL[m.templateKey] || m.templateKey }} · <span class="ad-mono">{{ m.toAddress }}</span>
                  </span>
                  <span class="err__meta">{{ m.lastError || '—' }} · {{ formatDateTime(m.createdAt) }}<template v-if="m.attempts"> · {{ m.attempts }} intento{{ m.attempts === 1 ? '' : 's' }}</template></span>
                </div>
                <button type="button" class="ad-btn ad-btn--ghost small" :disabled="busy[m.id]" @click="retry(m)">Reenviar</button>
              </div>
            </li>
          </ul>
          <p v-else class="ad-empty">No hay mensajes pendientes de atención.</p>
          <p v-if="comms.recent.length >= 30" class="ad-hint footnote">Se muestran los 30 más recientes. Los mensajes se conservan 90 días.</p>
        </template>
      </div>
    </section>

    <!-- WhatsApp de la plataforma -->
    <section>
      <div class="ad-toolbar sec-head">
        <h2 class="sec-title">WhatsApp</h2>
        <span class="ad-toolbar__spacer"></span>
        <button type="button" class="ad-btn ad-btn--ghost" :disabled="waBusy" @click="waVerify">Probar conexión</button>
        <button type="button" class="ad-btn ad-btn--ghost" :disabled="waBusy || !wa || !wa.live" @click="waSync">Actualizar estados</button>
        <button type="button" class="ad-btn ad-btn--primary" :disabled="waBusy || !wa || !wa.live" @click="waSubmit">Enviar plantillas a Meta</button>
      </div>
      <div class="ad-card">
        <p v-if="waLoading && !wa" class="ad-empty">Cargando…</p>
        <p v-else-if="waError" class="ad-empty">{{ waError }}</p>
        <template v-else-if="wa">
          <div class="comms">
            <div class="comms__prov">
              <span class="comms__label">Proveedor</span>
              <span class="ad-pill" :class="wa.live ? 'ad-pill--green' : 'ad-pill--gray'">{{ wa.live ? 'Meta Cloud API' : 'Modo consola' }}</span>
              <span class="comms__label">Número</span>
              <span class="ad-mono">{{ wa.displayPhone || '—' }}</span>
              <span class="comms__label">Plantillas aprobadas</span>
              <span class="ad-mono">{{ wa.approved }} / {{ wa.templates.length }}</span>
              <span class="comms__label">Aceptaron avisos</span>
              <span class="ad-mono">{{ wa.optedIn }}</span>
              <span class="comms__label">Bajas (BAJA)</span>
              <span class="ad-mono">{{ wa.optedOut }}</span>
            </div>
          </div>
          <p v-if="wa.missing.length" class="ad-hint comms__note">Faltan en el .env del Backend: {{ wa.missing.join(', ') }}.</p>
          <p v-else-if="!wa.live" class="ad-hint comms__note">Sin proveedor, los WhatsApp no salen. Para activarlo pon WHATSAPP_PROVIDER=meta, WHATSAPP_TOKEN, WHATSAPP_PHONE_NUMBER_ID y WHATSAPP_BUSINESS_ACCOUNT_ID en el .env del Backend.</p>
          <p v-else-if="!wa.hasBusinessAccountId" class="ad-hint comms__note">Falta WHATSAPP_BUSINESS_ACCOUNT_ID para poder enviar y consultar plantillas.</p>
          <p class="ad-hint comms__note">URL del webhook en Meta: <span class="ad-mono">{{ wa.webhookUrlPath }}</span> (dominio público del Backend + esta ruta).</p>

          <ul class="errors">
            <li v-for="t in wa.templates" :key="t.key" class="err">
              <div class="err__row">
                <span class="ad-pill" :class="TPL_PILL[t.status]">{{ TPL_TEXT[t.status] }}</span>
                <div class="err__main">
                  <span class="err__title"><b>{{ t.description }}</b> · <span class="ad-mono">{{ t.metaName }}</span></span>
                  <span class="err__meta">{{ t.body }}</span>
                  <span v-if="t.reason" class="err__meta tpl-reason">{{ t.reason }}</span>
                </div>
              </div>
            </li>
          </ul>
        </template>
      </div>
    </section>

    <div v-if="toast" class="ad-toast" role="status">{{ toast }}</div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { getSystemHealth, listSystemErrors, reviewSystemError, getCommunications, retryCommunications, getWhatsAppPlatform, verifyWhatsApp, submitWhatsAppTemplates, syncWhatsAppTemplates } from '@/services/superadmin'
import { relativeTime, formatDateTime, errorText } from '@/components/Superadmin/adminFormat'

const OVERALL_TEXT = { OK: 'Todo funciona', WARN: 'Requiere atención', FAIL: 'Hay fallas', OFF: '—' }
const LEVEL_TEXT = { OK: 'Operativo', WARN: 'Atención', FAIL: 'Falla', OFF: 'Sin configurar' }

const health = ref(null)
const healthLoading = ref(false)
const healthError = ref('')

const errors = ref([])
const pending = ref(0)
const includeReviewed = ref(false)
const errorsLoading = ref(true)
const errorsError = ref('')
const open = ref({})
const busy = ref({})
const toast = ref('')

const TEMPLATE_LABEL = {
  invitation_admin: 'Invitación de administrador', invitation_staff: 'Invitación al equipo', invitation_tenant: 'Invitación al portal',
  statement: 'Estado de cuenta', payment_reminder: 'Recordatorio de pago', new_charge: 'Nuevo cargo',
  ticket_assigned: 'Ticket asignado', preventive_assigned: 'Mantenimiento preventivo', ticket_registered: 'Solicitud registrada', ticket_received: 'Solicitud recibida',
}
const comms = ref(null)
const commsLoading = ref(true)
const commsError = ref('')
const retrying = ref(false)

const TPL_TEXT = { NOT_SUBMITTED: 'Sin enviar', PENDING: 'En revisión', APPROVED: 'Aprobada', REJECTED: 'Rechazada', PAUSED: 'Pausada' }
const TPL_PILL = { NOT_SUBMITTED: 'ad-pill--gray', PENDING: 'ad-pill--amber', APPROVED: 'ad-pill--green', REJECTED: 'ad-pill--red', PAUSED: 'ad-pill--amber' }
const wa = ref(null)
const waLoading = ref(true)
const waError = ref('')
const waBusy = ref(false)

function say(msg, ms = 4000) {
  toast.value = msg
  setTimeout(() => { toast.value = '' }, ms)
}

async function loadWa() {
  waLoading.value = true
  waError.value = ''
  try {
    wa.value = await getWhatsAppPlatform()
  } catch (e) {
    waError.value = errorText(e, 'No se pudo consultar el estado de WhatsApp.')
  } finally {
    waLoading.value = false
  }
}

async function waAction(fn, done) {
  waBusy.value = true
  try {
    say(await done(await fn()))
    await Promise.all([loadWa(), loadHealth()])
  } catch (e) {
    say(errorText(e))
  } finally {
    waBusy.value = false
  }
}

const waVerify = () => waAction(verifyWhatsApp, (r) => (r.success
  ? `Conexión exitosa${r.displayPhone ? ` · ${r.displayPhone}` : ''}${r.verifiedName ? ` · ${r.verifiedName}` : ''}`
  : r.message))
const waSubmit = () => waAction(submitWhatsAppTemplates, ({ results }) => {
  const ok = results.filter((r) => r.result === 'SUBMITTED').length
  const bad = results.filter((r) => r.result === 'ERROR')
  return bad.length ? `${ok} enviadas, ${bad.length} con error: ${bad[0].detail}` : ok ? `${ok} plantilla${ok === 1 ? '' : 's'} enviada${ok === 1 ? '' : 's'} a Meta` : 'No había plantillas por enviar'
})
const waSync = () => waAction(syncWhatsAppTemplates, ({ updated }) => `${updated} plantilla${updated === 1 ? '' : 's'} actualizada${updated === 1 ? '' : 's'}`)

async function loadHealth() {
  healthLoading.value = true
  healthError.value = ''
  try {
    health.value = await getSystemHealth()
  } catch (e) {
    healthError.value = errorText(e, 'No se pudo revisar el estado de los servicios.')
  } finally {
    healthLoading.value = false
  }
}

async function loadErrors() {
  errorsLoading.value = true
  errorsError.value = ''
  try {
    const res = await listSystemErrors(includeReviewed.value)
    errors.value = res.items
    pending.value = res.pending
  } catch (e) {
    errorsError.value = errorText(e, 'No se pudieron cargar los errores.')
  } finally {
    errorsLoading.value = false
  }
}

const toggle = (id) => { open.value = { ...open.value, [id]: !open.value[id] } }

async function markReviewed(e) {
  busy.value = { ...busy.value, [e.id]: true }
  try {
    await reviewSystemError(e.id)
    toast.value = `Error ${e.ref} marcado como revisado`
    setTimeout(() => { toast.value = '' }, 3000)
    await Promise.all([loadErrors(), loadHealth()])
  } catch (err) {
    toast.value = errorText(err)
    setTimeout(() => { toast.value = '' }, 4000)
  } finally {
    busy.value = { ...busy.value, [e.id]: false }
  }
}

async function loadComms() {
  commsLoading.value = true
  commsError.value = ''
  try {
    comms.value = await getCommunications()
  } catch (e) {
    commsError.value = errorText(e, 'No se pudo consultar el estado de las comunicaciones.')
  } finally {
    commsLoading.value = false
  }
}

// Con un mensaje reenvía solo ese; sin argumento, todos los fallidos y sin enviar.
async function retry(m) {
  if (m) busy.value = { ...busy.value, [m.id]: true }
  else retrying.value = true
  try {
    const res = await retryCommunications(m ? { id: m.id } : {})
    toast.value = res.requeued ? `${res.requeued} mensaje${res.requeued === 1 ? '' : 's'} en cola de nuevo` : 'No había nada que reenviar'
    setTimeout(() => { toast.value = '' }, 3500)
    // La cola procesa en segundo plano: se espera un momento para ver el resultado.
    setTimeout(() => { loadComms(); loadHealth() }, 1500)
  } catch (err) {
    toast.value = errorText(err)
    setTimeout(() => { toast.value = '' }, 4000)
  } finally {
    if (m) busy.value = { ...busy.value, [m.id]: false }
    else retrying.value = false
  }
}

onMounted(() => { loadHealth(); loadErrors(); loadComms(); loadWa() })
</script>

<style scoped>
.sec-head { margin-bottom: 10px; }
.sec-title { margin: 0; font: 700 15px var(--font-display); color: #1e3a5f; }

.dot { display: inline-block; width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; background: #cbd5e1; }
.dot--OK { background: #10b981; }
.dot--WARN { background: #f59e0b; }
.dot--FAIL { background: #e84545; }
.dot--OFF { background: #cbd5e1; }

.overall { display: inline-flex; align-items: center; gap: 6px; font: 600 12px Inter, sans-serif; margin-left: 6px; }
.overall--OK { color: #047857; }
.overall--WARN { color: #b45309; }
.overall--FAIL { color: #b91c1c; }

.checks { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 12px; }
.check { padding: 14px 16px; display: flex; align-items: center; gap: 12px; }
.check__body { display: flex; flex-direction: column; gap: 2px; flex: 1; min-width: 0; }
.check__label { font: 600 13px Inter, sans-serif; color: #1f2937; }
.check__detail { font: 400 12px Inter, sans-serif; color: #64748b; }
.check__level { font: 600 11px Inter, sans-serif; white-space: nowrap; color: #94a3b8; }
.check__level--OK { color: #047857; }
.check__level--WARN { color: #b45309; }
.check__level--FAIL { color: #b91c1c; }

.toggle { display: inline-flex; align-items: center; gap: 6px; font: 500 12px Inter, sans-serif; color: #64748b; cursor: pointer; }

.errors { list-style: none; margin: 0; padding: 0; }
.err { padding: 12px 18px; border-top: 1px solid #f1f5f9; }
.err:first-child { border-top: none; }
.err--reviewed { opacity: 0.6; }
.err__row { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.err__ref { font-weight: 700; color: #1e3a5f; background: #eef2f7; padding: 3px 8px; border-radius: 5px; font-size: 12px; letter-spacing: 0.04em; }
.err__main { display: flex; flex-direction: column; gap: 2px; flex: 1; min-width: 220px; }
.err__title { font: 400 13px Inter, sans-serif; color: #1f2937; word-break: break-all; }
.err__meta { font: 400 11px Inter, sans-serif; color: #94a3b8; }
.err__details { margin: 10px 0 0; padding: 10px 12px; background: #0f172a; color: #e2e8f0; border-radius: 6px; font: 400 11px/1.5 ui-monospace, Menlo, Consolas, monospace; white-space: pre-wrap; word-break: break-word; max-height: 260px; overflow: auto; }
.small { padding: 5px 10px; font-size: 12px; }
.footnote { margin: 0; padding: 8px 18px 12px; }

.comms { display: flex; align-items: center; justify-content: space-between; gap: 20px; flex-wrap: wrap; padding: 16px 18px; }
.comms__prov { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.comms__label { font: 500 12px Inter, sans-serif; color: #64748b; }
.comms__prov .comms__label:not(:first-child) { margin-left: 10px; }
.comms__counts { display: flex; gap: 22px; }
.count { display: flex; flex-direction: column; align-items: flex-end; gap: 1px; }
.count b { font-size: 20px; color: #1e3a5f; }
.count span { font: 400 11px Inter, sans-serif; color: #94a3b8; }
.count--warn b { color: #b45309; }
.count--bad b { color: #b91c1c; }
.comms__note { margin: 0; padding: 0 18px 12px; }
.tpl-reason { color: #b91c1c; }
</style>